import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { helpQueries, HelpArticle, HelpSection } from '../../lib/queries';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { T, sans } from '../Vendors/VendorDetails';

/**
 * Sidebar "Help": the step-by-step platform guide. Content comes from GET /v1/help
 * (backend/services/help_content.py), which only answers for paid workspaces -- a free trial gets 403 and
 * this page shows the upgrade note instead. `?article=<id>` deep-links to one article.
 */

const palette = (isDark: boolean) => ({
  isDark,
  ink: isDark ? '#e2e8f0' : T.ink,
  body: isDark ? '#cbd5e1' : T.ink2,
  slate: isDark ? '#94a3b8' : T.slate,
  muted: isDark ? '#7c8aa0' : T.muted,
  line: isDark ? '#1f1f1f' : T.bdr,
  line2: isDark ? '#171717' : '#EEF2F6',
  surface: isDark ? '#0d0d0d' : T.surface,
  surface2: isDark ? '#141414' : T.surface2,
  hover: isDark ? '#141414' : '#F1F5F9',
  bg: isDark ? '#000000' : T.bg,
  accent: isDark ? '#60A5FA' : '#2563EB',
  activeBg: isDark ? '#0C2A4A' : '#E6F1FB',
  activeInk: isDark ? '#DBEAFE' : '#0C447C',
});
type P = ReturnType<typeof palette>;

// One hue per section, used only for its small icon chip and step numbers.
const SECTION_HUE: Record<string, string> = {
  'getting-started': '#2563EB',
  vendors: '#0891B2',
  scans: '#7C3AED',
  'pre-onboarding': '#D97706',
  questionnaires: '#059669',
  risk: '#DC2626',
  insights: '#DB2777',
  'my-organization': '#4F46E5',
  workspace: '#475569',
  faq: '#0D9488',
};
const hueOf = (id: string) => SECTION_HUE[id] || '#2563EB';

const SECTION_GLYPH: Record<string, string> = {
  'getting-started': '★', vendors: '▦', scans: '◎', 'pre-onboarding': '⇥', questionnaires: '☑',
  risk: '⚑', insights: '▤', 'my-organization': '⌂', workspace: '⚙', faq: '?',
};

function SectionChip({ id, size = 28 }: { id: string; size?: number }) {
  const hue = hueOf(id);
  return (
    <span style={{
      width: size, height: size, borderRadius: 8, flexShrink: 0, display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center', background: `${hue}1A`, color: hue, fontSize: size * 0.5, fontWeight: 700,
    }}>{SECTION_GLYPH[id] || '•'}</span>
  );
}

const matches = (a: HelpArticle, q: string) =>
  [a.title, a.summary, a.what, a.why, ...a.steps, ...(a.tips || [])].join(' ').toLowerCase().includes(q);

export default function Help() {
  const { mode } = useCustomTheme();
  const c = palette(mode === 'dark');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  const guideQuery = useQuery(helpQueries.guide());
  const sections = guideQuery.data?.sections ?? [];
  const articleId = params.get('article');

  const located = useMemo(() => {
    for (const s of sections) {
      const a = s.articles.find(x => x.id === articleId);
      if (a) return { section: s, article: a };
    }
    return null;
  }, [sections, articleId]);

  const flat = useMemo(() => sections.flatMap(s => s.articles.map(a => ({ section: s, article: a }))), [sections]);
  const position = located ? flat.findIndex(x => x.article.id === located.article.id) : -1;
  const prev = position > 0 ? flat[position - 1] : null;
  const next = position >= 0 && position < flat.length - 1 ? flat[position + 1] : null;

  const q = query.trim().toLowerCase();
  const results = useMemo(() => (q ? flat.filter(x => matches(x.article, q)) : []), [flat, q]);

  const open = (id: string | null) => {
    const nextParams = new URLSearchParams(params);
    if (id) nextParams.set('article', id); else nextParams.delete('article');
    setParams(nextParams);
    setQuery('');
  };

  useEffect(() => { contentRef.current?.scrollTo?.({ top: 0 }); window.scrollTo({ top: 0 }); }, [articleId]);

  const status = (guideQuery.error as any)?.response?.status;

  const page: React.CSSProperties = { fontFamily: sans, color: c.ink, background: c.bg, minHeight: 'calc(100vh - 64px)', margin: -24, padding: 24 };
  const panel: React.CSSProperties = { background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12 };

  if (status === 403) {
    return (
      <div style={page}>
        <div style={{ ...panel, maxWidth: 560, margin: '60px auto', padding: '36px 32px', textAlign: 'center' }}>
          <div style={{ fontSize: 34, marginBottom: 10 }}>🔒</div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Help guides are part of paid plans</div>
          <div style={{ fontSize: 13.5, color: c.slate, lineHeight: 1.6 }}>
            Step-by-step guides for every part of FluxWatch are included with every paid plan. Upgrade your workspace to get access.
          </div>
          <button onClick={() => navigate('/settings?section=plan')} style={{
            marginTop: 18, height: 36, padding: '0 16px', borderRadius: 8, border: 'none', background: c.accent,
            color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: sans,
          }}>See plans</button>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' }}>Help</div>
          <div style={{ fontSize: 13, color: c.slate, marginTop: 4 }}>
            Step-by-step guides for every part of FluxWatch{guideQuery.data ? ` · ${guideQuery.data.article_count} guides` : ''}
          </div>
        </div>
        <div style={{ position: 'relative', width: 'min(380px, 100%)' }}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search the guides…"
            aria-label="Search the guides"
            style={{
              width: '100%', boxSizing: 'border-box', height: 38, padding: '0 12px 0 34px', borderRadius: 9,
              border: `1px solid ${c.line}`, background: c.surface, color: c.ink, fontSize: 13.5, fontFamily: sans, outline: 'none',
            }}
          />
          <span style={{ position: 'absolute', left: 12, top: 10, color: c.muted, fontSize: 14 }}>⌕</span>
        </div>
      </div>

      {guideQuery.isPending ? (
        <div style={{ ...panel, padding: '48px 0', textAlign: 'center', fontSize: 13, color: c.muted }}>Loading guides…</div>
      ) : guideQuery.isError ? (
        <div style={{ ...panel, padding: '48px 0', textAlign: 'center', fontSize: 13, color: c.muted }}>
          Couldn't load the guides. <button onClick={() => guideQuery.refetch()} style={{ border: 'none', background: 'none', color: c.accent, cursor: 'pointer', fontSize: 13 }}>Try again</button>
        </div>
      ) : (
        <div className="help-layout" style={{ display: 'grid', gridTemplateColumns: '260px minmax(0, 1fr)', gap: 18, alignItems: 'start' }}>
          <style>{`@media (max-width: 860px) { .help-layout { grid-template-columns: 1fr !important; } .help-nav { position: static !important; max-height: none !important; } }`}</style>

          {/* Contents */}
          <nav className="help-nav" aria-label="Help contents" style={{
            ...panel, padding: 8, position: 'sticky', top: 88, maxHeight: 'calc(100vh - 112px)', overflowY: 'auto',
          }}>
            <NavRow c={c} active={!articleId && !q} onClick={() => open(null)}>
              <span style={{ fontWeight: 600 }}>All guides</span>
            </NavRow>
            {sections.map(s => (
              <div key={s.id} style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px 4px' }}>
                  <SectionChip id={s.id} size={20} />
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: c.muted }}>{s.title}</span>
                </div>
                {s.articles.map(a => (
                  <NavRow key={a.id} c={c} active={a.id === articleId && !q} onClick={() => open(a.id)} indent>
                    {a.title}
                  </NavRow>
                ))}
              </div>
            ))}
          </nav>

          {/* Main */}
          <div ref={contentRef} style={{ minWidth: 0 }}>
            {q ? (
              <SearchResults c={c} q={q} results={results} onOpen={open} />
            ) : located ? (
              <ArticleView
                c={c} section={located.section} article={located.article}
                prev={prev?.article || null} next={next?.article || null}
                onOpen={open} onGo={path => navigate(path)} panel={panel}
              />
            ) : (
              <Home c={c} sections={sections} onOpen={open} panel={panel} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NavRow({ c, active, onClick, indent, children }: { c: P; active: boolean; onClick: () => void; indent?: boolean; children: React.ReactNode }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-current={active ? 'page' : undefined}
      style={{
        display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer', fontFamily: sans,
        padding: indent ? '6px 10px 6px 36px' : '8px 10px', borderRadius: 7, fontSize: 13, lineHeight: 1.35,
        background: active ? c.activeBg : hover ? c.hover : 'transparent',
        color: active ? c.activeInk : c.body, fontWeight: active ? 600 : 400,
      }}
    >{children}</button>
  );
}

function Home({ c, sections, onOpen, panel }: { c: P; sections: HelpSection[]; onOpen: (id: string) => void; panel: React.CSSProperties }) {
  const first = sections[0]?.articles[0];
  return (
    <div>
      {first && (
        <div style={{ ...panel, padding: '20px 22px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 15.5, fontWeight: 700 }}>New to FluxWatch?</div>
            <div style={{ fontSize: 13, color: c.slate, marginTop: 4, lineHeight: 1.55 }}>
              Start with what FluxWatch does, then follow the setup checklist. Every guide below is a short, numbered walkthrough.
            </div>
          </div>
          <button onClick={() => onOpen(first.id)} style={{
            height: 36, padding: '0 16px', borderRadius: 8, border: 'none', background: c.accent, color: '#fff',
            fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: sans,
          }}>Start here →</button>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        {sections.map(s => (
          <div key={s.id} style={{ ...panel, padding: '16px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <SectionChip id={s.id} />
              <div style={{ fontSize: 14.5, fontWeight: 700 }}>{s.title}</div>
            </div>
            <div style={{ fontSize: 12.5, color: c.slate, margin: '8px 0 10px', lineHeight: 1.5 }}>{s.summary}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {s.articles.map(a => (
                <button key={a.id} onClick={() => onOpen(a.id)} style={{
                  textAlign: 'left', border: 'none', background: 'none', padding: '4px 0', cursor: 'pointer',
                  color: c.accent, fontSize: 13, fontFamily: sans,
                }}>{a.title}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SearchResults({ c, q, results, onOpen }: {
  c: P; q: string; results: { section: HelpSection; article: HelpArticle }[]; onOpen: (id: string) => void;
}) {
  return (
    <div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '12px 18px', borderBottom: `1px solid ${c.line}`, fontSize: 12.5, color: c.slate }}>
        {results.length} {results.length === 1 ? 'guide matches' : 'guides match'} “{q}”
      </div>
      {results.length === 0 ? (
        <div style={{ padding: '36px 18px', textAlign: 'center', fontSize: 13, color: c.muted }}>No guides match. Try a different word.</div>
      ) : results.map(({ section, article }) => (
        <button key={article.id} onClick={() => onOpen(article.id)} style={{
          display: 'flex', gap: 12, width: '100%', textAlign: 'left', padding: '12px 18px', border: 'none',
          borderBottom: `1px solid ${c.line2}`, background: 'transparent', cursor: 'pointer', fontFamily: sans,
        }}>
          <SectionChip id={section.id} size={24} />
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: c.ink }}>{article.title}</span>
            <span style={{ display: 'block', fontSize: 12.5, color: c.slate, marginTop: 2 }}>{section.title} · {article.summary}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

function ArticleView({ c, section, article, prev, next, onOpen, onGo, panel }: {
  c: P; section: HelpSection; article: HelpArticle; prev: HelpArticle | null; next: HelpArticle | null;
  onOpen: (id: string) => void; onGo: (path: string) => void; panel: React.CSSProperties;
}) {
  const hue = hueOf(section.id);
  const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: c.muted, marginBottom: 6 };
  const para: React.CSSProperties = { fontSize: 14, color: c.body, lineHeight: 1.65, margin: 0 };
  return (
    <article style={{ ...panel, padding: '24px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: c.slate }}>
        <SectionChip id={section.id} size={22} />
        <span>{section.title}</span>
      </div>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '12px 0 4px', letterSpacing: '-0.01em' }}>{article.title}</h1>
      <div style={{ fontSize: 14, color: c.slate }}>{article.summary}</div>

      <div style={{ display: 'grid', gridTemplateColumns: article.why ? 'repeat(auto-fit, minmax(240px, 1fr))' : '1fr', gap: 20, marginTop: 22 }}>
        <div>
          <div style={label}>What it is</div>
          <p style={para}>{article.what}</p>
        </div>
        {article.why && (
          <div>
            <div style={label}>What it's for</div>
            <p style={para}>{article.why}</p>
          </div>
        )}
      </div>

      <div style={{ ...label, marginTop: 26, marginBottom: 12 }}>Step by step</div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {article.steps.map((step, i) => (
          <li key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{
              width: 24, height: 24, borderRadius: 99, flexShrink: 0, display: 'inline-flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 12, fontWeight: 700, color: hue, background: `${hue}1A`, marginTop: 1,
            }}>{i + 1}</span>
            <span style={{ ...para, fontSize: 14 }}>{step}</span>
          </li>
        ))}
      </ol>

      {!!article.tips?.length && (
        <div style={{ marginTop: 24 }}>
          <div style={label}>Good to know</div>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {article.tips.map((t, i) => <li key={i} style={{ ...para, fontSize: 13.5 }}>{t}</li>)}
          </ul>
        </div>
      )}

      {article.path && (
        <button onClick={() => onGo(article.path!)} style={{
          marginTop: 24, height: 36, padding: '0 16px', borderRadius: 8, border: `1px solid ${c.line}`,
          background: c.surface, color: c.accent, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: sans,
        }}>Open this page →</button>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 28, paddingTop: 16, borderTop: `1px solid ${c.line}` }}>
        {prev ? (
          <button onClick={() => onOpen(prev.id)} style={pager(c)}>
            <span style={{ fontSize: 11, color: c.muted }}>← Previous</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: c.ink }}>{prev.title}</span>
          </button>
        ) : <span />}
        {next && (
          <button onClick={() => onOpen(next.id)} style={{ ...pager(c), alignItems: 'flex-end', textAlign: 'right' }}>
            <span style={{ fontSize: 11, color: c.muted }}>Next →</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: c.ink }}>{next.title}</span>
          </button>
        )}
      </div>
    </article>
  );
}

const pager = (c: P): React.CSSProperties => ({
  display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start', textAlign: 'left', border: `1px solid ${c.line}`,
  background: c.surface, borderRadius: 9, padding: '8px 14px', cursor: 'pointer', fontFamily: sans, maxWidth: '48%',
});
